import { useEffect, useMemo, useState } from "react";
import { MessageSquare, X } from "lucide-react";
import { Modal } from "../../components/ui";
import {
  TICKET_CATEGORIES,
  childLabel,
  parentOptionLabel,
  teacherOptionLabel,
} from "./supportHelpers";
import "../teachers/addTeacher.css";

const EMPTY_FORM = {
  participantRole: "TEACHER",
  participant: "",
  relatedChild: "",
  subject: "",
  category: "GENERAL",
  priority: "NORMAL",
  message: "",
};

const EMPTY_INITIAL_VALUES = {};

export default function CreateTicketModal({
  open,
  onClose,
  onSubmit,
  creating = false,
  initialValues = EMPTY_INITIAL_VALUES,
  teachers = [],
  parents = [],
  children = [],
  loadingLists = false,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY_FORM, ...initialValues });
    setError("");
  }, [open, initialValues]);

  const contactOptions = useMemo(() => {
    const source = form.participantRole === "PARENT" ? parents : teachers;
    return source.map((item) => ({
      value: item._id,
      label:
        form.participantRole === "PARENT"
          ? parentOptionLabel(item)
          : teacherOptionLabel(item),
    }));
  }, [form.participantRole, parents, teachers]);

  const relatedChildren = useMemo(() => {
    if (form.participantRole !== "PARENT" || !form.participant) return [];
    return children.filter(
      (item) => String(item.parent?._id || item.parent) === String(form.participant)
    );
  }, [children, form.participant, form.participantRole]);

  const handleSubmit = () => {
    if (!form.participant || !form.subject.trim() || !form.message.trim()) {
      setError("Pick a contact, add a subject, and write a message.");
      return;
    }
    setError("");
    onSubmit(form);
  };

  const handleClose = () => {
    if (creating) return;
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      closable={false}
      className="support-create-modal-wrap"
      style={{ maxWidth: 520, width: "min(520px, 92vw)", padding: 0 }}
      destroyOnClose
    >
      <div className="support-create-modal">
        <div className="support-create-head">
          <div className="support-create-head-copy">
            <span className="support-create-icon">
              <MessageSquare size={20} />
            </span>
            <div>
              <h2>New message</h2>
              <p>Send a message to a teacher or parent.</p>
            </div>
          </div>
          <button type="button" className="support-create-close" onClick={handleClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="support-create-form">
          <div className="support-create-field">
            <label className="tf-label">Send to</label>
            <div className="support-role-toggle">
              <button
                type="button"
                className={`support-role-btn${form.participantRole === "TEACHER" ? " is-active" : ""}`}
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    participantRole: "TEACHER",
                    participant: "",
                    relatedChild: "",
                  }))
                }
              >
                Teacher
              </button>
              <button
                type="button"
                className={`support-role-btn${form.participantRole === "PARENT" ? " is-active" : ""}`}
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    participantRole: "PARENT",
                    participant: "",
                    relatedChild: "",
                  }))
                }
              >
                Parent
              </button>
            </div>
            <select
              className="support-create-select"
              value={form.participant}
              disabled={loadingLists}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  participant: event.target.value,
                  relatedChild: "",
                }))
              }
            >
              <option value="">
                {loadingLists
                  ? "Loading..."
                  : `Select a ${form.participantRole === "PARENT" ? "parent" : "teacher"}`}
              </option>
              {contactOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          {form.participantRole === "PARENT" && form.participant && relatedChildren.length > 0 ? (
            <div className="support-create-field">
              <label className="tf-label">About student (optional)</label>
              <select
                className="support-create-select"
                value={form.relatedChild}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, relatedChild: event.target.value }))
                }
              >
                <option value="">No specific student</option>
                {relatedChildren.map((item) => (
                  <option key={item._id} value={item._id}>
                    {childLabel(item)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="support-create-field">
            <label className="tf-label">Category</label>
            <select
              className="support-create-select"
              value={form.category}
              onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))}
            >
              {TICKET_CATEGORIES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div className="support-create-field">
            <label className="tf-label">
              Subject <span>*</span>
            </label>
            <input
              className="support-create-input"
              value={form.subject}
              onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
              placeholder="What is this about?"
            />
          </div>

          <div className="support-create-field">
            <label className="tf-label">
              Describe your issue <span>*</span>
            </label>
            <textarea
              className="support-create-textarea"
              rows={4}
              value={form.message}
              onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
              placeholder="Please provide as much detail as possible"
            />
          </div>

          <div className="support-urgent-row">
            <div>
              <p>Mark as urgent</p>
              <span>Flags this ticket as urgent for the office.</span>
            </div>
            <button
              type="button"
              className={`support-switch${form.priority === "URGENT" ? " is-on" : ""}`}
              role="switch"
              aria-checked={form.priority === "URGENT"}
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  priority: prev.priority === "URGENT" ? "NORMAL" : "URGENT",
                }))
              }
            >
              <span />
            </button>
          </div>

          {error ? <p className="support-create-error">{error}</p> : null}
        </div>

        <div className="support-create-foot">
          <button type="button" className="data-secondary" disabled={creating} onClick={handleClose}>
            Cancel
          </button>
          <button type="button" className="data-primary" disabled={creating} onClick={handleSubmit}>
            {creating ? "Sending..." : "Submit ticket"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export { EMPTY_FORM as CREATE_TICKET_EMPTY_FORM };
